import { createContext, FC, useContext, useEffect, useMemo, useState } from "react";
import { OASContextProps } from "../../../interfaces/SuperAdmin/ContextProps/iOAS";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/Auth";
import { iDataOrganizacion } from "../../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { getAllOrganizations, getOrganizationById } from "../../../api/broker";
import { toast } from "react-toastify";
import { SelectChangeEvent } from "@mui/material";
import { iTipoObjetivo } from "../../../interfaces/SuperAdmin/Others/iDataTipoObjetivos";
import { getAllTiposObjetivo } from "../../../api/broker/tipo_objetivo";

const OASContext = createContext<OASContextProps>({} as OASContextProps);

interface Props {
  children: React.ReactNode;
  organizationId?: string | null;
}

const OASProvider: FC<Props> = ({ children, organizationId  }) => {
  const params = useParams();
  const idFromRoute = params.idOrganization;
  const { canAccess } = useAuthContext();

  const [organizaciones, setOrganizaciones] = useState<iDataOrganizacion[]>([]);
  const [organizacion, setOrganizacion] = useState<iDataOrganizacion | null>(
    null
  );
  const [tiposObjetivo, setTiposObjetivo] = useState<iTipoObjetivo[]>([]);
 const initialOrgId = organizationId ?? idFromRoute; // Se prioriza organizacionId para la carga de tabs
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(initialOrgId);

  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingOthers, setIsLoadingOthers] = useState(false);

  const navigate = useNavigate();

  // Fetch de organizaciones para seleccionar
  const fetchOrgs = async () => {
    setIsLoadingOrgs(true);
    try {
      const orgsRes = await getAllOrganizations();
      console.log(orgsRes);

      if (orgsRes.data.statusCode === 200) {
        setOrganizaciones(orgsRes.data.body);
      } else {
        toast.error("Error al obtener las organizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las organizaciones");
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  // Fetch de una organización y los tipos de objetivo
  const fetchOthers = async (id: string) => {
    setIsLoadingOthers(true);
    try {
      const [orgsRes, tiposRes] = await Promise.all([
        getOrganizationById(id),
        getAllTiposObjetivo(),
      ]);

      console.log(orgsRes);

      if (orgsRes.data.statusCode === 200) {
        setOrganizacion(orgsRes.data.body);
      } else {
        toast.error("Error al obtener la organización");
      }

      console.log(tiposRes);

      if (tiposRes.data.statusCode === 200) {
        setTiposObjetivo(tiposRes.data.body);
      } else {
        toast.error("Error al obtener los tipos de objetivo");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener la organización y/o los tipos de objetivo");
    } finally {
      setIsLoadingOthers(false);
    }
  };

  useEffect(() => {
    const isSuperAdmin = canAccess("view:oas");
    if (isSuperAdmin) fetchOrgs();
  }, []);

  useEffect(() => {
  const next = organizationId ?? idFromRoute;
  if (next && next !== selectedOrg) setSelectedOrg(next);
}, [organizationId, idFromRoute]);

  const handleSelectChange = async (event: SelectChangeEvent) => {
    setSelectedOrg(event.target.value);
    navigate(`/asistente/oas/${event.target.value}`);
  };

  // Función para traer los tipos de objetivos totales y ver a cuáles tiene acceso la organización
  useEffect(() => {
    if (selectedOrg) {
      fetchOthers(selectedOrg);
    }
  }, [selectedOrg]);

   const allowedTP = useMemo(() => {
    const orgTipoObjetivo = organizacion?.tipo_objetivo;

    if (!Array.isArray(orgTipoObjetivo)) return false;

    const tpTipo = (tiposObjetivo ?? []).find((t) => t.codigo === "TP");
    if (!tpTipo?.id_tipo_objetivo) return false;

    return orgTipoObjetivo.includes(tpTipo.id_tipo_objetivo);
  }, [organizacion, tiposObjetivo]);

  const isLoadingAllowedTP = isLoadingOthers && !organizacion;

  return (
    <OASContext.Provider
      value={{
        organizaciones,
        organizacion,
        tiposObjetivo,
        selectedOrg,
        isLoadingOrgs,
        isLoadingOthers,
        handleSelectChange,
        allowedTP,
        isLoadingAllowedTP,
      }}
    >
      {children}
    </OASContext.Provider>
  );
};

const useOAS = () => useContext(OASContext);

export { OASProvider, useOAS };
